import React from 'react';
import { Printer } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTranslation } from '../i18n';
import { categories, Category } from '../data/priceData';
function CategoryBlock({
  category,
  lang
}: {
  category: Category;
  lang: 'en' | 'ua' | 'ru' | 'ro';
}) {
  return <div className="mb-2.5 break-inside-avoid" data-id="element-3101" data-mp="zngytm4n4x2j">
      <div className="bg-forest-700 text-white text-[8.5px] font-bold uppercase tracking-wider px-2 py-1 mb-1" data-id="element-3102" data-mp="zl49gnwjzkfg">
        {category.title[lang]}
      </div>
      <div className="space-y-[3px] px-0.5" data-id="element-3103" data-mp="18mu03rim2fc6">
        {category.items.map((item, idx) => <div key={idx} className="flex items-baseline gap-0.5" data-id="element-3104" data-mp="1dec1ggojqhxo" data-mpi={idx}>
            <span className="text-[9px] text-charcoal leading-tight flex-shrink-0" style={{
          maxWidth: '72%'
        }} data-id="element-3105" data-mp="1idox9qxeazce">
              {item.name[lang]}
            </span>
            <span className="flex-grow border-b border-dotted border-beige-dark/60 mx-0.5 relative top-[-1px]" data-id="element-3106" data-mp="1scxpae8pa3w4" />
            <span className="text-[9px] font-bold text-forest-700 whitespace-nowrap tabular-nums flex-shrink-0" data-id="element-3107" data-mp="2d69tyw1aw1kz">
              {item.price}
            </span>
          </div>)}
      </div>
    </div>;
}
function splitCategoriesEvenly(list: Category[]): [Category[], Category[]] {
  // Each category = heading (~1.5 rows) + its items
  const weight = (cat: Category) => cat.items.length + 1.5;
  const total = list.reduce((sum, cat) => sum + weight(cat), 0);
  let running = 0;
  let bestIndex = 1;
  let bestDiff = Infinity;
  list.forEach((cat, idx) => {
    running += weight(cat);
    const diff = Math.abs(total - 2 * running);
    if (diff < bestDiff) {
      bestDiff = diff;
      bestIndex = idx + 1;
    }
  });
  return [list.slice(0, bestIndex), list.slice(bestIndex)];
}
export function PrintPricePage() {
  const {
    lang
  } = useTranslation();
  const handlePrint = () => window.print();
  const today = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
  const currentLang = lang as 'en' | 'ua' | 'ru' | 'ro';
  // Prices come only from the shared price data used by the Prices page.
  // Columns are balanced by row count (category order is kept) so the A4 page doesn't overflow as items are added.
  const [leftColumn, rightColumn] = splitCategoriesEvenly(categories);
  return <div className="min-h-screen bg-gray-100 p-6 print:p-0 print:bg-white" data-id="element-3108" data-mp="1354hlsw7izou">
      {/* Controls */}
      <div className="no-print max-w-[210mm] mx-auto mb-6 flex items-center justify-between" data-id="element-3109" data-mp="pa28yxzz6w8c">
        <div data-id="element-3110" data-mp="u4g1msn96p9b">
          <h1 className="text-xl font-bold text-gray-900" data-id="element-3111" data-mp="f9rxbhtl9wva">
            Dentico Clinic — Price List
          </h1>
          <p className="text-sm text-gray-500 mt-0.5" data-id="element-3112" data-mp="k838bvvmedf3">
            Tip: Enable <strong data-id="element-3113" data-mp="kafy2v7mds2p">Background graphics</strong> in print settings
            for best results.
          </p>
        </div>
        <Button onClick={handlePrint} className="gap-2 shadow-md" data-id="element-3114" data-mp="2emdzwc3lnlok">
          <Printer className="w-4 h-4" data-id="element-3115" data-mp="2ekjk1c76qjrc" />
          Print Price List
        </Button>
      </div>

      {/* A4 Page — fixed height to match exactly one A4 */}
      <div className="price-page bg-white mx-auto shadow-xl print:shadow-none flex flex-col" style={{
      width: '210mm',
      height: '297mm'
    }} data-id="element-3116" data-mp="3u6mvp7kmyq8z">
        {/* Compact Header — matches website header brand lockup */}
        <div className="border-b-2 border-forest-700 px-8 pt-5 pb-3 flex-shrink-0" data-id="element-3117" data-mp="lps3cm1gso95">
          <div className="flex items-center justify-between" data-id="element-3118" data-mp="lrmpu4sdwofc">
            <div className="flex items-center gap-3" data-id="element-3119" data-mp="hnezy4a6x2fj">
              {/* Brand wordmark — architectural lockup, forest palette */}
              <div className="flex flex-col leading-none" data-id="element-3120" data-mp="vlve2gadtv7p">
                <span className="text-[26px] font-semibold tracking-[0.22em] text-forest-700" style={{
                fontFamily: '"Playfair Display", Georgia, serif'
              }} data-id="element-3121" data-mp="qpwjuc2dhizd">
                  DENTICO
                </span>
                <span className="block text-right text-[8px] tracking-[0.4em] text-forest-700 uppercase mt-0.5" data-id="element-3122" data-mp="2twdjv7tg5vud">
                  Clinic
                </span>
              </div>
              <div className="border-l border-beige-dark/60 pl-3" data-id="element-3123" data-mp="33v1jyrlh7q9s">
                <div className="text-[9px] text-charcoal-light uppercase tracking-widest leading-relaxed" data-id="element-3124" data-mp="33tqln7asvaq2">
                  Private Dentistry · London, Angel
                  <br data-id="element-3125" data-mp="2b88lygzh153h" />
                  360–364 City Road, EC1V 2PY
                </div>
              </div>
            </div>
            <div className="text-right" data-id="element-3126" data-mp="2g3wacu64pu7x">
              <div className="text-xl font-bold text-forest-900 uppercase tracking-wide leading-none" data-id="element-3127" data-mp="2l4k0ldmja713">
                Price List
              </div>
              <div className="text-[9px] text-stone-500 mt-1" data-id="element-3128" data-mp="2pxwkxbk6jro1">
                {today} · denticoclinic.co.uk
              </div>
            </div>
          </div>
        </div>

        {/* Two-column price grid — fills remaining space */}
        <div className="flex-grow px-8 pt-4 pb-2 grid grid-cols-2 gap-x-6 items-start overflow-hidden" data-id="element-3129" data-mp="ek4p4m8ubf4k">
          <div data-id="element-3130" data-mp="ai9nthqwt1qs">
            {leftColumn.map(cat => <CategoryBlock key={cat.id} category={cat} lang={currentLang} data-id="element-3131" data-mp="agoqxlh4h6pr" />)}
          </div>
          <div data-id="element-3132" data-mp="5lj7quf3u65d">
            {rightColumn.map(cat => <CategoryBlock key={cat.id} category={cat} lang={currentLang} data-id="element-3133" data-mp="jttl5wl5cd2" />)}
          </div>
        </div>

        {/* Compact Footer */}
        <div className="flex-shrink-0 mx-8 pt-2 pb-4 border-t border-gray-200" data-id="element-3134" data-mp="3n07w0ps4ozru">
          <p className="text-[8px] text-gray-400 text-center leading-relaxed" data-id="element-3135" data-mp="3my3h7csoygcs">
            Prices are indicative and may vary. A consultation is required
            before treatment. &nbsp;·&nbsp; +44 7345 197820 (WhatsApp)
            &nbsp;·&nbsp; denticoclinic.co.uk &nbsp;·&nbsp; Book:
            denticoclinic.patientportal.onl/bookings
          </p>
        </div>
      </div>

      <style data-id="element-3136" data-mp="3423xsqds4l4t">{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 0;
          }
          html, body {
            margin: 0;
            padding: 0;
            background: white;
          }
          .no-print {
            display: none !important;
          }
          .price-page {
            width: 210mm !important;
            height: 297mm !important;
            box-shadow: none !important;
            margin: 0 !important;
            overflow: hidden !important;
          }
        }
      `}</style>
    </div>;
}